import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'hooks/use-redux'
import { useLocation, useParams, useNavigate } from 'react-router-dom'
import type { TrackId } from 'types/track'
import { getOrFetchTrack } from '@disco/state-management/actions/PlaylistTrackActions'
import { getTrack } from '@disco/state-management/selectors/tracks'
import DocumentHeadTags from 'components/document-head-tags'
import AggregatedTrackShareModal from 'components/modals/track/aggregated-track-share'
import TrackShareModal from 'components/modals/track/share'
import Heading from 'components/ui/heading'
import ModalWindow from 'components/ui/modal-window'
import Spinner from 'components/ui/spinner'

const TrackShare = (): React.ReactNode => {
  const location = useLocation()
  const params =
    useParams<{
      trackId: string
      sourceLibraryId?: string
    }>()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const trackId = parseInt(params.trackId!, 10) as TrackId
  const track = useSelector(state =>
    getTrack(state, {
      id: trackId,
    }),
  )

  const onClose = () => navigate('/')

  useEffect(() => {
    if (!track) {
      if (
        params.sourceLibraryId &&
        !isNaN(parseInt(params.sourceLibraryId, 10))
      ) {
        dispatch(getOrFetchTrack(trackId, parseInt(params.sourceLibraryId, 10)))
      } else {
        dispatch(getOrFetchTrack(trackId))
      }
    }
  }, [track, trackId, params.sourceLibraryId, dispatch])

  if (!track) {
    return (
      <>
        <DocumentHeadTags title="Share track | DISCO" />
        <ModalWindow
          header={<Heading size={700}>Share Track</Heading>}
          width="medium"
          onClose={onClose}
          isOpen
        >
          <Spinner />
        </ModalWindow>
      </>
    )
  }

  const searchParams = new URLSearchParams(location.search)
  const queryType = searchParams.get('type')

  return (
    <>
      <DocumentHeadTags title="Share track | DISCO" />
      {params.sourceLibraryId ? (
        <AggregatedTrackShareModal track={track} onClose={onClose} />
      ) : (
        <TrackShareModal
          type={queryType}
          track={track}
          onClose={onClose}
          sharedChannel={params.sourceLibraryId === 'shared-channel'}
        />
      )}
    </>
  )
}

export default TrackShare
